import React, { useState, useMemo } from "react";
import { View, Pressable, StyleSheet, ActivityIndicator, ScrollView } from "react-native";
import { FileText } from "lucide-react";
import CustomModal from "../../../common/general/CustomModal/CustomModal";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import { useGetAssignmentsWithCourseQuery } from "../../../../graphql/generated/graphql";
import { useAppConfig } from "../../../../AppConfigProvider";

export interface Assignment {
  id: string;
  title?: string | null;
  text?: string | null;
  due_date?: string | null;
  points_possible?: number | null;
  courseName?: string;
  courseCode?: string;
  courseId?: string;
}

interface AddAssignmentModalProps {
  visible: boolean;
  onClose: () => void;
  onSelectAssignment: (assignment: Assignment) => void;
}

export const AddAssignmentModal: React.FC<AddAssignmentModalProps> = ({ visible, onClose, onSelectAssignment }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const appConfig = useAppConfig();
  const schoolId = appConfig?.axioSchools[0];

  const { data, loading } = useGetAssignmentsWithCourseQuery({
    variables: {
      schoolId,
      searchQuery: searchQuery.trim(),
    },
    skip: !schoolId,
    fetchPolicy: "network-only",
  });

  const groupedAssignments = useMemo(() => {
    if (!data?.getAssignmentsWithCourse?.data) return [];

    const grouped: {
      [key: string]: { courseName: string; courseCode?: string | null; assignments: Assignment[] };
    } = {};

    data.getAssignmentsWithCourse.data.forEach((assignment) => {
      const courseName = assignment.courseName || "Other";
      const courseCode = assignment.courseCode;
      if (!grouped[courseName]) {
        grouped[courseName] = { courseName, courseCode, assignments: [] };
      }
      grouped[courseName].assignments.push(assignment as Assignment);
    });

    return Object.values(grouped);
  }, [data]);

  const handleSelect = (assignment: Assignment) => {
    onSelectAssignment(assignment);
    setSearchQuery("");
    onClose();
  };

  const renderAssignmentItem = ({ item }: { item: Assignment }) => {
    const isHovered = hoveredId === item.id;
    return (
      <Pressable
        style={[styles.assignmentItem, isHovered && styles.assignmentItemHovered]}
        onPress={() => handleSelect(item)}
        onMouseEnter={() => setHoveredId(item.id)}
        onMouseLeave={() => setHoveredId(null)}>
        <View style={styles.assignmentContent}>
          <View style={styles.assignmentTitleRow}>
            <FileText size={20} color="#3e68fe" style={styles.assignmentIcon} />
            <CustomText
              text={item.title || "Untitled Assignment"}
              useTranslationText={false}
              style={styles.assignmentName}
              numberOfLines={1}
            />
          </View>
          {item.due_date && (
            <CustomText
              text={`Due ${new Date(item.due_date).toLocaleDateString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
              })}`}
              useTranslationText={false}
              style={styles.assignmentDate}
            />
          )}
        </View>
      </Pressable>
    );
  };

  const renderCourseSection = ({
    item,
  }: {
    item: { courseName: string; courseCode?: string | null; assignments: Assignment[] };
  }) => (
    <View style={styles.courseSection}>
      <CustomText
        text={item.courseCode ? `${item.courseName} (${item.courseCode})` : item.courseName}
        useTranslationText={false}
        style={styles.courseName}
      />
      {item.assignments.map((assignment) => (
        <View key={assignment.id}>{renderAssignmentItem({ item: assignment })}</View>
      ))}
    </View>
  );

  const hasSearch = searchQuery.trim().length > 0;
  const hasData = groupedAssignments.length > 0;

  return (
    <CustomModal
      visible={visible}
      onRequestClose={onClose}
      title="Add Assignment"
      showCloseButton
      ariaLabel="Add Assignment Modal">
      <View style={styles.modalContent}>
        <CustomText
          text="Select an assignment from your courses to attach to your message"
          useTranslationText={false}
          style={styles.subtitle}
        />

        <CustomTextInput
          placeholder="Search assignments..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          style={styles.searchInput}
        />

        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#3e68fe" />
          </View>
        ) : !hasData ? (
          <View style={styles.emptyContainer}>
            <CustomText
              text={hasSearch ? "No assignments found" : "No assignments available"}
              useTranslationText={false}
              style={styles.emptyText}
            />
          </View>
        ) : (
          <ScrollView style={styles.assignmentList} contentContainerStyle={styles.assignmentListContent}>
            {groupedAssignments.map((item) => (
              <View key={item.courseName}>{renderCourseSection({ item })}</View>
            ))}
          </ScrollView>
        )}
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  modalContent: {
    width: 700,
    maxHeight: 600,
  },
  subtitle: {
    fontSize: 14,
    color: "#667085",
    marginBottom: 20,
  },
  searchInput: {
    marginBottom: 16,
  },
  assignmentList: {
    maxHeight: 400,
  },
  assignmentListContent: {
    paddingTop: 16,
    paddingBottom: 4,
  },
  courseSection: {
    marginBottom: 20,
  },
  courseName: {
    fontSize: 12,
    fontWeight: "600",
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 8,
    paddingHorizontal: 4,
  },
  assignmentItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 16,
    marginBottom: 8,
    cursor: "pointer",
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  assignmentItemHovered: {
    backgroundColor: "#E6EBFF",
    borderColor: "#6788FE",
  },
  assignmentContent: {
    flex: 1,
  },
  assignmentTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  assignmentIcon: {
    marginRight: 8,
  },
  assignmentName: {
    fontSize: 14,
    color: "#101828",
    fontWeight: "500",
    flex: 1,
  },
  assignmentDate: {
    fontSize: 12,
    color: "#667085",
    marginLeft: 28,
  },
  loadingContainer: {
    paddingVertical: 60,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyContainer: {
    paddingVertical: 60,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    fontSize: 14,
    color: "#667085",
  },
});
